<template>
  <Teleport to="body">
    <transition name="fade" appear>
      <div
        v-if="isOpen"
        class="modal-backdrop"
        @click="closeOnBackdropClick"
      ></div>
    </transition>

    <transition name="slide-up" appear>
      <div
        v-if="isOpen"
        class="custom-modal"
        :class="{ 'compact-mode': isCompactMode }"
      >
        <div class="modal-content" :class="contentClass">
          <div
            class="close-button bg-zinc-200 dark:bg-zinc-800"
            @click="$emit('close')"
            v-if="showCloseButton"
          >
            <div class="close-icon"></div>
          </div>

          <div class="flex flex-col items-center modal-inner-content">
            <slot></slot>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  backdropDismiss: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  contentClass: {
    type: String,
    default: "bg-white dark:bg-black",
  },
  forceCompactMode: {
    type: Boolean,
    default: false,
  },
});

const isCompactMode = ref(false);

const closeOnBackdropClick = () => {
  if (props.backdropDismiss) {
    emit("close");
  }
};

// Handle screen resize
const handleResize = () => {
  // Only consider it compact mode if width is mobile-sized (< 768px) OR height is very constrained
  isCompactMode.value =
    props.forceCompactMode ||
    window.innerWidth < 768 ||
    window.innerHeight <= 600;
};

onMounted(() => {
  // Add keydown listener for Escape key
  document.addEventListener("keydown", handleKeyDown);

  // Check initial screen size
  handleResize();

  // Add resize listener
  window.addEventListener("resize", handleResize);

  // Prevent scrolling on body when modal is open
  document.body.style.overflow = "hidden";
});

onUnmounted(() => {
  // Remove event listeners
  document.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("resize", handleResize);

  // Restore scrolling
  document.body.style.overflow = "";
});

// Watch for isOpen changes to manage body scroll
watch(
  () => props.isOpen,
  (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "";
  }
);

// Handle Escape key to close modal
const handleKeyDown = (e) => {
  if (e.key === "Escape" && props.isOpen && props.backdropDismiss) {
    emit("close");
  }
};

const emit = defineEmits(["close"]);
</script>

<style scoped lang="scss">
@import "@/assets/styles/mixins.scss";

// Animation for backdrop and modal
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

// Modal backdrop
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1000;
}

// Custom modal - base styles (shared across all modes)
.custom-modal {
  position: fixed;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  margin: 0 auto;
  width: 90%;
  max-width: 500px;
  z-index: 1001;

  // Modal content - base styles
  .modal-content {
    margin: auto;
    position: relative;
    border-radius: 20px;
    width: 100%;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .modal-inner-content {
    padding: 2em;
    margin-bottom: 2em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: auto;
    overflow: hidden;
  }

  // Close button styles
  .close-button {
    border-radius: 9999px;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: absolute;
    top: 1em;
    right: 1em;
    z-index: 10;

    .close-icon {
      position: relative;
      width: 16px;
      height: 16px;

      &:before,
      &:after {
        content: "";
        position: absolute;
        height: 16px;
        width: 2px;
        top: 0;
        left: 7px;
        background-color: #666;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }

  // Compact mode overrides
  &.compact-mode {
    top: 0;
    bottom: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    overflow: visible;
    pointer-events: none; // Allow clicks to pass through to backdrop

    .modal-content {
      pointer-events: auto; // Re-enable pointer events for content
      border-radius: 20px;
      max-height: min(600px, calc(100% - 2em));
      height: auto;
      overflow: hidden;
      // Only go full width on truly mobile devices
      width: calc(min(500px, 100% - 2em));
      margin: 0 auto;
      z-index: 1001;
    }

    .modal-inner-content {
      padding: 1.75em;
      margin-bottom: 0;
    }
  }
}

// Dark mode adjustments
body.dark {
  .custom-modal .close-button .close-icon {
    &:before,
    &:after {
      background-color: #ccc;
    }
  }
}
</style>
